/**
 * Utilities for generating descriptive alt text for images
 * Following WCAG 2.1 guidelines for meaningful alternative text
 */

export interface ProductImageAltParams {
  /** Color/variant name (e.g., "Ice White", "Black") */
  colorName?: string
  /** Device name (e.g., "iPhone 16 Pro Max") */
  deviceName?: string
  /** Image index for numbered variants */
  imageIndex?: number
  /** Type of image */
  imageType?: "action" | "detail" | "gallery" | "lifestyle"
  /** Product name (e.g., "Pathfinder", "Monarch Pro") */
  productName: string
  /** Series name if applicable */
  seriesName?: string
}

/**
 * Higher-order function to generate alt text for a product variant image
 * Useful for mapping over arrays of images
 *
 * @param product - Product data
 * @param variant - Variant data
 * @returns Function that takes image URL and index and returns alt text
 */
export function createProductImageAltGenerator(
  product: {
    name: string
    seriesName?: string
  },
  variant: {
    colorName?: string
    deviceName?: string
  }
) {
  return (imageUrl: string, index: number): string => {
    const isLifestyleImage = imageUrl.includes("lifestyle") // Adjust based on your URL patterns
    const isActionImage = imageUrl.includes("action")

    return getProductImageAlt({
      colorName: variant.colorName,
      deviceName: variant.deviceName,
      imageIndex: index,
      imageType: isLifestyleImage
        ? "lifestyle"
        : isActionImage
          ? "action"
          : index === 0
            ? "gallery"
            : "detail",
      productName: product.name,
      seriesName: product.seriesName,
    })
  }
}

/**
 * Generates alt text from Contentful image title field
 * Falls back to empty string if no title provided
 *
 * @param title - Contentful image title
 * @returns Sanitized alt text
 */
export function getContentfulImageAlt(title?: string): string {
  if (!title) {
    return ""
  }
  return sanitizeAlt(title)
}

/**
 * Returns empty string for decorative images
 * Use this for backgrounds, borders, spacers, etc.
 *
 * @returns Empty string
 */
export function getDecorativeAlt(): string {
  return ""
}

/**
 * Generates alt text for product action/lifestyle images
 *
 * Examples:
 * - "Person using UAG Pathfinder case showing drop protection"
 * - "UAG case in action demonstrating wireless charging"
 *
 * @param params - Product action image parameters
 * @returns Descriptive alt text
 */
export function getProductActionImageAlt(params: {
  actionDescription?: string
  productName: string
}): string {
  const { actionDescription, productName } = params

  if (actionDescription) {
    return `UAG ${productName} ${actionDescription}`
  }

  return `UAG ${productName} in action`
}

/**
 * Generates descriptive alt text for product images
 *
 * Examples:
 * - "UAG Pathfinder iPhone 16 Pro Max case in Ice White"
 * - "UAG Monarch Pro iPad case showing leather texture"
 * - "UAG Plasma case detail view"
 *
 * @param params - Product image parameters
 * @returns Descriptive alt text (< 125 characters)
 */
export function getProductImageAlt(params: ProductImageAltParams): string {
  const { colorName, deviceName, imageType, productName, seriesName } = params

  // Build base description
  const parts: string[] = []

  // Always start with UAG brand
  parts.push("UAG")

  // Add series if provided
  if (seriesName && !productName.includes(seriesName)) {
    parts.push(seriesName)
  }

  // Add product name
  parts.push(productName)

  // Add device name if provided
  if (deviceName) {
    parts.push(deviceName)
  }

  // Add "case" if not already in product name
  if (!productName.toLowerCase().includes("case")) {
    parts.push("case")
  }

  // Add color/variant if provided
  if (colorName) {
    parts.push(`in ${colorName}`)
  }

  // Add image type context for lifestyle/action shots
  if (imageType === "lifestyle" || imageType === "action") {
    parts.push("lifestyle view")
  } else if (imageType === "detail") {
    parts.push("detail view")
  }

  const alt = parts.join(" ")

  // Ensure we stay under 125 characters (screen reader limit)
  return alt.length > 125 ? alt.substring(0, 122) + "..." : alt
}

/**
 * Generates alt text for cart/thumbnail product images
 *
 * Example: "UAG Pathfinder case thumbnail"
 *
 * @param productName - Product name
 * @returns Concise alt text for thumbnails
 */
export function getProductThumbnailAlt(productName: string): string {
  return `UAG ${productName} case thumbnail`
}

/**
 * Sanitizes and validates alt text
 * - Removes redundant phrases like "image of", "picture of"
 * - Trims whitespace
 * - Ensures reasonable length
 *
 * @param alt - Raw alt text
 * @returns Cleaned alt text
 */
export function sanitizeAlt(alt: string): string {
  return alt
    .replace(/^(image of|picture of|photo of)\s+/i, "")
    .replace(/\s+/g, " ")
    .trim()
}
